import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Camera, Upload, X, Loader2, Edit2, Check, AlertTriangle, Shield, Eye } from 'lucide-react';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';

const IncidentPhotoUpload = ({ reportId, photos = [], onPhotosChange, disabled = false, pendingPhotos = [], onPendingPhotosChange }) => {
  const [uploading, setUploading] = useState(false);
  const [editingPhotoId, setEditingPhotoId] = useState(null);
  const [editFindings, setEditFindings] = useState(null);
  const [savingFindings, setSavingFindings] = useState(false);
  const fileInputRef = useRef(null);

  const handleUpload = async (files) => {
    if (!reportId) {
      // Pre-save mode: store files locally as base64 for later upload
      const newPending = [...(pendingPhotos || [])];
      for (const file of files) {
        if (!file.type.startsWith('image/')) {
          toast.error(`${file.name} is not an image`);
          continue;
        }
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name} is too large (max 10MB)`);
          continue;
        }
        const base64 = await fileToBase64(file);
        newPending.push({
          id: `pending-${Date.now()}-${Math.random().toString(36).slice(2)}`,
          filename: file.name,
          preview: base64,
          file: file,
          status: 'pending'
        });
      }
      if (onPendingPhotosChange) onPendingPhotosChange(newPending);
      toast.success(`${files.length} photo(s) ready — will be uploaded & analyzed when you run AI Analysis`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Normal mode: upload to server immediately
    setUploading(true);
    const newPhotos = [...photos];

    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image`);
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 10MB)`);
        continue;
      }

      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axiosInstance.post(
          `/incident-reports/${reportId}/analyze-photo`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 }
        );

        if (response.data.success) {
          newPhotos.push(response.data.photo);
          toast.success('Photo uploaded & analyzed');
        }
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    onPhotosChange(newPhotos);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.readAsDataURL(file);
    });
  };

  const removePendingPhoto = (id) => {
    if (onPendingPhotosChange) {
      onPendingPhotosChange((pendingPhotos || []).filter(p => p.id !== id));
    }
  };

  const handleDelete = async (photoId) => {
    if (!reportId) return;
    try {
      await axiosInstance.delete(`/incident-reports/${reportId}/photos/${photoId}`);
      onPhotosChange(photos.filter(p => p.id !== photoId));
      toast.success('Photo removed');
    } catch (error) {
      toast.error('Failed to remove photo');
    }
  };

  const startEditFindings = (photo) => {
    setEditingPhotoId(photo.id);
    setEditFindings({ ...photo.aiFindings });
  };

  const clearAllFindings = () => {
    setEditFindings(prev => ({
      ...prev,
      summary: '',
      hazards: [],
      damage: [],
      oshaViolations: [],
      safetyObservations: []
    }));
    toast.info('AI findings cleared — enter your own observations, then save');
  };

  const saveFindings = async (photoId) => {
    setSavingFindings(true);
    const payload = {
      ...editFindings,
      hazards: editFindings?.hazards || [],
      damage: editFindings?.damage || [],
      oshaViolations: editFindings?.oshaViolations || [],
      safetyObservations: editFindings?.safetyObservations || [],
      userEdited: true,
      editedAt: new Date().toISOString()
    };
    try {
      await axiosInstance.put(
        `/incident-reports/${reportId}/photos/${photoId}/findings`,
        payload
      );
      onPhotosChange(photos.map(p =>
        p.id === photoId ? { ...p, aiFindings: payload } : p
      ));
      setEditingPhotoId(null);
      setEditFindings(null);
      toast.success('Findings updated — your version will be used on the report');
    } catch (error) {
      toast.error('Failed to save findings');
    } finally {
      setSavingFindings(false);
    }
  };

  const severityColor = (severity) => {
    const map = { critical: 'bg-red-600', high: 'bg-red-500', medium: 'bg-yellow-500', low: 'bg-green-500' };
    return map[severity] || 'bg-gray-400';
  };

  const allPending = pendingPhotos || [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">Incident Photos</label>
        <span className="text-xs text-gray-500">
          {photos.length + allPending.length} photo(s)
          {allPending.length > 0 && <span className="text-amber-600 ml-1">({allPending.length} pending upload)</span>}
        </span>
      </div>

      {/* Upload area */}
      <div
        className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
          disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-blue-400 cursor-pointer'
        }`}
        onClick={() => !disabled && !uploading && fileInputRef.current?.click()}
        data-testid="photo-upload-area"
      >
        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            <p className="text-sm text-blue-600">Uploading & analyzing with AI...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <div className="flex gap-3">
              <Camera className="h-6 w-6 text-gray-400" />
              <Upload className="h-6 w-6 text-gray-400" />
            </div>
            <p className="text-sm text-gray-600">
              Click to take or upload photos
            </p>
            <p className="text-xs text-gray-400">JPG, PNG, WebP up to 10MB each</p>
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          capture="environment"
          multiple
          className="hidden"
          onChange={(e) => handleUpload(Array.from(e.target.files))}
          disabled={disabled || uploading}
          data-testid="photo-file-input"
        />
      </div>

      {/* Pending photos (pre-save) */}
      {allPending.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-amber-700 bg-amber-50 p-2 rounded">
            Photos will be uploaded & AI-analyzed when you click "Run AI Analysis"
          </p>
          <div className="grid grid-cols-3 gap-2">
            {allPending.map((photo) => (
              <div key={photo.id} className="relative">
                <img
                  src={photo.preview}
                  alt={photo.filename}
                  className="w-full h-24 object-cover rounded-lg border"
                />
                <button
                  onClick={(e) => { e.stopPropagation(); removePendingPhoto(photo.id); }}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                >
                  <X className="h-3 w-3" />
                </button>
                <Badge className="absolute bottom-1 left-1 bg-amber-500 text-[9px]">Pending</Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Uploaded photos with AI findings */}
      {photos.length > 0 && (
        <div className="space-y-4">
          {photos.map((photo) => (
            <Card key={photo.id} className="overflow-hidden" data-testid={`photo-card-${photo.id}`}>
              <div className="flex flex-col sm:flex-row">
                {/* Photo */}
                <div className="sm:w-48 h-40 sm:h-auto bg-gray-100 flex-shrink-0 relative">
                  <img
                    src={photo.url}
                    alt={photo.filename}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  {!disabled && (
                    <button
                      onClick={() => handleDelete(photo.id)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      data-testid={`delete-photo-${photo.id}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>

                {/* AI Findings */}
                <div className="flex-1 p-3">
                  {editingPhotoId === photo.id ? (
                    /* Editable mode */
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-blue-700">Editing Findings</span>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" className="text-xs text-red-600 h-7"
                            onClick={clearAllFindings}
                            data-testid={`clear-findings-${photo.id}`}>
                            Clear AI text
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => { setEditingPhotoId(null); setEditFindings(null); }}>
                            <X className="h-3 w-3" />
                          </Button>
                          <Button size="sm" onClick={() => saveFindings(photo.id)} disabled={savingFindings}
                            data-testid={`save-findings-${photo.id}`}>
                            {savingFindings ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                          </Button>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-medium">Summary</label>
                        <textarea value={editFindings?.summary || ''} rows={2}
                          onChange={(e) => setEditFindings({ ...editFindings, summary: e.target.value })}
                          className="w-full border rounded px-2 py-1 text-xs mt-0.5"
                          data-testid={`edit-summary-${photo.id}`} />
                      </div>
                      <div>
                        <label className="text-xs font-medium">Hazards (one per line)</label>
                        <textarea value={(editFindings?.hazards || []).join('\n')} rows={2}
                          onChange={(e) => setEditFindings({ ...editFindings, hazards: e.target.value.split('\n').filter(Boolean) })}
                          className="w-full border rounded px-2 py-1 text-xs mt-0.5"
                          data-testid={`edit-hazards-${photo.id}`} />
                      </div>
                      <div>
                        <label className="text-xs font-medium">Damage (one per line)</label>
                        <textarea value={(editFindings?.damage || []).join('\n')} rows={2}
                          onChange={(e) => setEditFindings({ ...editFindings, damage: e.target.value.split('\n').filter(Boolean) })}
                          className="w-full border rounded px-2 py-1 text-xs mt-0.5"
                          data-testid={`edit-damage-${photo.id}`} />
                      </div>
                      <div>
                        <label className="text-xs font-medium">OSHA Violations (one per line)</label>
                        <textarea value={(editFindings?.oshaViolations || []).join('\n')} rows={2}
                          onChange={(e) => setEditFindings({ ...editFindings, oshaViolations: e.target.value.split('\n').filter(Boolean) })}
                          className="w-full border rounded px-2 py-1 text-xs mt-0.5"
                          data-testid={`edit-osha-${photo.id}`} />
                      </div>
                      <div>
                        <label className="text-xs font-medium">Safety Observations (one per line)</label>
                        <textarea value={(editFindings?.safetyObservations || []).join('\n')} rows={2}
                          onChange={(e) => setEditFindings({ ...editFindings, safetyObservations: e.target.value.split('\n').filter(Boolean) })}
                          className="w-full border rounded px-2 py-1 text-xs mt-0.5"
                          placeholder="Leave empty to remove this section from the report"
                          data-testid={`edit-observations-${photo.id}`} />
                      </div>
                      <div>
                        <label className="text-xs font-medium">Severity</label>
                        <select value={editFindings?.severity || 'medium'}
                          onChange={(e) => setEditFindings({ ...editFindings, severity: e.target.value })}
                          className="w-full border rounded px-2 py-1 text-xs mt-0.5">
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                          <option value="critical">Critical</option>
                        </select>
                      </div>
                    </div>
                  ) : (
                    /* View mode */
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Eye className="h-3 w-3 text-blue-500" />
                          <span className="text-xs font-semibold text-blue-700">
                            {photo.aiFindings?.userEdited ? 'Photo Findings (your version)' : 'AI Photo Analysis'}
                          </span>
                          {photo.aiFindings?.severity && (
                            <Badge className={`text-[10px] text-white ${severityColor(photo.aiFindings.severity)}`}>
                              {photo.aiFindings.severity.toUpperCase()}
                            </Badge>
                          )}
                        </div>
                        {!disabled && (
                          <Button size="sm" variant="ghost" onClick={() => startEditFindings(photo)}
                            data-testid={`edit-findings-${photo.id}`}>
                            <Edit2 className="h-3 w-3 mr-1" />
                            <span className="text-xs">Edit / remove</span>
                          </Button>
                        )}
                      </div>
                      {photo.aiFindings?.summary && (
                        <p className="text-xs text-gray-700">{photo.aiFindings.summary}</p>
                      )}
                      {photo.aiFindings?.hazards?.length > 0 && (
                        <div>
                          <span className="text-[10px] font-semibold text-red-700 flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" /> Hazards
                          </span>
                          {photo.aiFindings.hazards.map((h, i) => (
                            <p key={i} className="text-xs text-gray-600 ml-4">- {h}</p>
                          ))}
                        </div>
                      )}
                      {photo.aiFindings?.damage?.length > 0 && (
                        <div>
                          <span className="text-[10px] font-semibold text-orange-700">Damage</span>
                          {photo.aiFindings.damage.map((d, i) => (
                            <p key={i} className="text-xs text-gray-600 ml-4">- {d}</p>
                          ))}
                        </div>
                      )}
                      {photo.aiFindings?.oshaViolations?.length > 0 && (
                        <div>
                          <span className="text-[10px] font-semibold text-purple-700 flex items-center gap-1">
                            <Shield className="h-3 w-3" /> OSHA Violations
                          </span>
                          {photo.aiFindings.oshaViolations.map((v, i) => (
                            <p key={i} className="text-xs text-gray-600 ml-4">- {v}</p>
                          ))}
                        </div>
                      )}
                      {photo.aiFindings?.safetyObservations?.length > 0 && (
                        <div data-testid={`observations-${photo.id}`}>
                          <span className="text-[10px] font-semibold text-amber-700">Safety Observations</span>
                          {photo.aiFindings.safetyObservations.map((o, i) => (
                            <p key={i} className="text-xs text-gray-600 ml-4">- {o}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default IncidentPhotoUpload;
